import { createElement, Fragment } from 'react';
import type { ZudokuConfig } from 'zudoku';
import { FooterBrand } from './src/FooterBrand';
import { FrenchUi } from './src/FrenchUi';
import { HeaderActions } from './src/HeaderActions';
import { HomePage } from './src/HomePage';
import { NotFound } from './src/NotFound';
import { WhiteLabel } from './src/WhiteLabel';
import { LiveUpdates } from './src/ProductUpdates';
import './src/noe-theme.css';

/**
 * « Noé — Centre d’aide »: product guides for conciergeries AND the developer
 * documentation, in one site (docs.wearenoe.fr).
 *
 * This file is evaluated both in Node (build) and in the browser, and it stays a
 * `.ts` file: the custom React components are mounted with `createElement`
 * rather than JSX, so the config needs no rename to `.tsx`.
 */

/**
 * The developer guides predate the help centre and used to live at the site
 * root (`/authentication`, `/oauth`…). They are now served under
 * `/developpeurs/…` through a custom `path` on each nav entry, which keeps the
 * MDX files where they were (and their content untouched). Every old URL is
 * redirected below, so links already in the wild keep working.
 */
const DEV_PAGES = [
  'scopes',
  'authentication',
  'quickstart',
  'apps',
  'oauth',
  'pagination',
  'filtering-and-sync',
  'pricing',
  'availability',
  'idempotency',
  'rate-limits',
  'errors',
  'webhooks',
  'changelog',
] as const;

const devDoc = (file: (typeof DEV_PAGES)[number]) => ({
  type: 'doc' as const,
  file,
  path: `/developpeurs/${file}`,
});

/** The API reference moved from `/api` to `/developpeurs/api` for the same reason. */
const API_PATH = '/developpeurs/api';
/**
 * One page per OpenAPI tag, as generated from `public-api.v1.json`. Listed so
 * the old `/api/<tag>` links redirect; add a tag here when the document gains one.
 */
const API_TAG_PAGES = [
  'account',
  'properties',
  'calendar',
  'blocks',
  'reservations',
  'webhooks',
  '~schemas',
];

const config: ZudokuConfig = {
  site: {
    title: 'Centre d’aide',
    // The wordmark from the tenant app, so the docs match the product. Note the
    // "dark" entry is the PALE variant: it is the one shown ON a dark
    // background, not the dark-coloured artwork.
    logo: {
      src: { light: '/noe-logo.png', dark: '/noe-logo-dark.png' },
      alt: 'Noé — Centre d’aide',
      width: '68px',
      href: '/',
    },
    showPoweredBy: false,
    notFoundPage: createElement(NotFound),
    footer: {
      position: 'start',
      columns: [
        {
          title: 'Centre d’aide',
          position: 'start',
          links: [
            { label: 'Site de réservation', href: '/site-de-reservation' },
            { label: 'Guide d’utilisation', href: '/guide' },
            { label: 'Nouveautés', href: '/guide/nouveautes' },
            { label: 'Développeurs', href: '/developpeurs' },
          ],
        },
        {
          title: 'Assistance',
          position: 'start',
          links: [
            { label: 'Dépannage', href: '/site-de-reservation/depannage' },
            { label: 'Questions fréquentes', href: '/site-de-reservation/faq' },
            { label: 'Se connecter à Noé', href: 'https://app.wearenoe.fr' },
          ],
        },
        {
          title: 'Noé',
          position: 'start',
          links: [
            { label: 'wearenoe.fr', href: 'https://wearenoe.fr' },
            { label: 'Mentions légales', href: 'https://wearenoe.fr/mentions-legales.html' },
            { label: 'Confidentialité', href: 'https://wearenoe.fr/confidentialite.html' },
          ],
        },
      ],
      copyright: `© ${new Date().getFullYear()} Noé. Tous droits réservés.`,
    },
  },

  metadata: {
    title: '%s | Centre d’aide Noé',
    description:
      'Le centre d’aide de Noé : guides pas à pas pour gérer votre conciergerie, lancer votre site de réservation directe, et documentation de l’API.',
    // Same mark the tenant app serves, so a pinned docs tab is recognisably Noé.
    // The app swaps its favicon per tenant for white-label; the docs are always
    // Noé-branded, so a static file is correct here.
    favicon: '/favicon.png',
  },

  /**
   * Noé brand, from the tenant app (`noe-app/src/index.css` + tailwind.config):
   * orange #FF5C1A, navy #1A1A2E, Inter for text and Plus Jakarta Sans for
   * headings. Everything beyond the colour tokens lives in `src/noe-theme.css`.
   */
  theme: {
    fonts: {
      sans: {
        url: 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=Plus+Jakarta+Sans:wght@500;600;700;800&display=swap',
        fontFamily: 'Inter, ui-sans-serif, system-ui, sans-serif',
      },
    },
    light: {
      background: '#ffffff',
      foreground: '#1a1a2e',
      card: '#ffffff',
      cardForeground: '#1a1a2e',
      popover: '#ffffff',
      popoverForeground: '#1a1a2e',
      primary: '#ff5c1a',
      primaryForeground: '#ffffff',
      secondary: '#f4f5f8',
      secondaryForeground: '#1a1a2e',
      muted: '#f4f5f8',
      mutedForeground: '#5b6072',
      accent: '#fff1ea',
      accentForeground: '#1a1a2e',
      border: '#e6e8ee',
      input: '#e2e4ea',
      ring: '#ff5c1a',
      radius: '0.75rem',
    },
    dark: {
      background: '#0e0e11',
      foreground: '#f2f2f7',
      card: '#17171b',
      cardForeground: '#f2f2f7',
      popover: '#1c1c21',
      popoverForeground: '#f2f2f7',
      primary: '#ff6b2c',
      primaryForeground: '#ffffff',
      secondary: '#1e1e23',
      secondaryForeground: '#f2f2f7',
      muted: '#1c1c21',
      mutedForeground: '#a3a3ad',
      accent: '#2a1810',
      accentForeground: '#f2f2f7',
      border: '#2a2a31',
      input: '#2f2f36',
      ring: '#ff6b2c',
      radius: '0.75rem',
    },
  },

  // Built at `zudoku build` time (Pagefind, static, no external service). In
  // `zudoku dev` the search dialog offers a « Build Search Index » button.
  search: {
    type: 'pagefind',
    maxSubResults: 3,
  },

  slots: {
    // Translates Zudoku's few hard-coded English UI strings and sets
    // <html lang="fr">; renders nothing.
    // FrenchUi first: WhiteLabel then rewrites the (French) page in a reseller's name on its docs.<root>.
    'head-navigation-start': createElement(Fragment, null, createElement(FrenchUi), createElement(WhiteLabel)),
    'head-navigation-end': createElement(HeaderActions),
    'footer-before': createElement(FooterBrand),
  },

  /**
   * Absolute origin for canonical tags, the sitemap and the llms.txt links.
   * Without it those come out relative, which is fine in a browser but useless
   * to an AI agent that fetched `llms.txt` and now has to resolve each entry.
   */
  canonicalUrlOrigin: 'https://docs.wearenoe.fr',

  sitemap: {
    siteUrl: 'https://docs.wearenoe.fr',
    changefreq: 'weekly',
  },

  redirects: [
    { from: '/introduction', to: '/developpeurs' },
    ...DEV_PAGES.map((page) => ({ from: `/${page}`, to: `/developpeurs/${page}` })),
    { from: '/api', to: API_PATH },
    ...API_TAG_PAGES.map((tag) => ({ from: `/api/${tag}`, to: `${API_PATH}/${tag}` })),
  ],

  /**
   * ROOT ITEMS BECOME TOP NAVIGATION TABS in Zudoku; only nested items build the
   * sidebar. Four tabs, one per audience:
   *
   * - « Accueil »: the landing page (custom React page, `src/HomePage.tsx`).
   * - « Guide d’utilisation »: product guides for conciergerie staff, in French.
   * - « Site de réservation »: the direct-booking website, its own tab because it
   *   is a product of its own with its own set-up journey.
   * - « Développeurs »: the public API guides (English) plus the API reference.
   *
   * Inside a tab, `section` headings group the pages without hiding them behind
   * collapsible categories.
   *
   * The Développeurs tab carries a category `link` (`/developpeurs`) and the API
   * reference sits UNDER that path (`/developpeurs/api`). That is what keeps the
   * tab highlighted — and its guides in the sidebar — while reading the
   * reference: Zudoku picks the active tab for plugin pages by path prefix.
   *
   * ONE SITE, TWO DEVELOPER PATHS — deliberately not two sites. There are two
   * kinds of integrator: someone wiring up a single workspace with an API key,
   * and someone building an app many workspaces connect to over OAuth. They
   * share the entire API reference and every core concept, so splitting would
   * duplicate two thirds of the content and leave two copies to drift apart
   * (see `scope-docs-drift.spec.ts`). What they need is to find THEIR path
   * quickly — solved by the two sections below and the fork at the top of
   * `introduction.mdx`.
   *
   * Note "partner" is ambiguous in this codebase: in these docs it means an API
   * integrator, while `Partner` in the API means a white-label reseller. Resellers
   * are users of the reseller console, not of an HTTP API — `/v1/partner/*` is an
   * internal backend for that SPA and is deliberately not published. If a reseller
   * integration API is ever built, it gets its own OpenAPI document listed in
   * `apis.input` below, not a separate site.
   */
  navigation: [
    {
      type: 'custom-page',
      path: '/',
      label: 'Accueil',
      icon: 'house',
      element: createElement(HomePage),
    },
    {
      type: 'category',
      label: 'Guide d’utilisation',
      icon: 'book-open',
      items: [
        { type: 'section', label: 'Commencer' },
        { type: 'doc', file: 'guide/index', path: '/guide' },
        'guide/premiers-pas',
        'guide/securite-connexion',
        'guide/nouveautes',
        { type: 'section', label: 'Au quotidien' },
        'guide/tableau-de-bord-et-statistiques',
        'guide/calendrier-et-tarifs',
        'guide/reservations',
        'guide/recherche-de-disponibilite',
        'guide/messagerie',
        'guide/voyageurs',
        'guide/automatisations',
        'guide/contrats-et-signatures',
        'guide/moyenne-duree',
        { type: 'section', label: 'Logements et prix' },
        'guide/logements',
        'guide/logements-equipements-photos',
        'guide/canaux',
        'guide/annonces-liees',
        'guide/tarification',
        'guide/bloquer-des-dates',
        { type: 'section', label: 'Terrain' },
        'guide/menage',
        'guide/menage-attribution',
        'guide/espace-intervenant',
        'guide/maintenance',
        'guide/entrepot',
        'guide/cles',
        'guide/check-apartment',
        { type: 'section', label: 'Argent et facturation' },
        'guide/paiements',
        'guide/paiements-comptes',
        'guide/paiements-proprietaires',
        'guide/paiements-attribution',
        'guide/paiements-depannage',
        'guide/cautions',
        'guide/ventes-additionnelles',
        'guide/facturation',
        'guide/facturation-parametres',
        'guide/factures-voyageurs',
        'guide/cockpit-financier',
        { type: 'section', label: 'Propriétaires' },
        'guide/proprietaires',
        'guide/espace-proprietaire',
        'guide/onboarding',
        { type: 'section', label: 'Expérience voyageur' },
        'guide/livret-d-accueil',
        'guide/avis',
        { type: 'section', label: 'Équipe et configuration' },
        'guide/utilisateurs',
        'guide/roles-et-permissions',
        'guide/planning-rh',
        'guide/apparence-et-e-mails',
        { type: 'section', label: 'Intégrations' },
        'guide/integrations',
        'guide/integrations/plateformes',
        'guide/integrations/pricelabs',
        'guide/integrations/igloohome',
        'guide/integrations/ttlock',
        'guide/integrations/google-calendar',
        'guide/integrations/paiements',
        'guide/integrations/faq',
        { type: 'section', label: 'Aide' },
        'guide/depannage-general',
      ],
    },
    {
      type: 'category',
      label: 'Site de réservation',
      icon: 'globe',
      items: [
        { type: 'section', label: 'Découvrir' },
        { type: 'doc', file: 'site-de-reservation/index', path: '/site-de-reservation' },
        'site-de-reservation/demarrer',
        { type: 'section', label: 'Vos logements et vos prix' },
        'site-de-reservation/logements-et-photos',
        'site-de-reservation/tarifs-et-disponibilites',
        'site-de-reservation/reservation-et-offres',
        'site-de-reservation/codes-promo',
        { type: 'section', label: 'Paiement et e-mails' },
        'site-de-reservation/paiements-et-encaissement',
        'site-de-reservation/emails',
        { type: 'section', label: 'Personnaliser votre site' },
        'site-de-reservation/apparence',
        'site-de-reservation/langues-et-devises',
        'site-de-reservation/seo-et-partage',
        'site-de-reservation/suivi-et-cookies',
        'site-de-reservation/widget-et-liens',
        'site-de-reservation/selections',
        { type: 'section', label: 'Mettre en ligne' },
        'site-de-reservation/domaine-personnalise',
        'site-de-reservation/publier-et-historique',
        { type: 'section', label: 'Au quotidien' },
        'site-de-reservation/quand-un-voyageur-reserve',
        'site-de-reservation/protection-contre-les-doubles-reservations',
        'site-de-reservation/espace-voyageur',
        { type: 'section', label: 'Aide' },
        'site-de-reservation/depannage',
        'site-de-reservation/bonnes-pratiques',
        'site-de-reservation/faq',
      ],
    },
    {
      type: 'category',
      label: 'Développeurs',
      icon: 'code',
      link: { type: 'doc', file: 'introduction', path: '/developpeurs' },
      items: [
        { type: 'section', label: 'Prise en main' },
        { type: 'link', to: '/developpeurs', label: 'Introduction' },
        devDoc('scopes'),
        { type: 'separator' },
        // The two audience paths, named so a reader can tell at a glance which is
        // theirs — two different jobs, not beginner/advanced.
        { type: 'section', label: 'Connecter un espace' },
        devDoc('authentication'),
        devDoc('quickstart'),
        { type: 'separator' },
        { type: 'section', label: 'Créer une app multi-espaces' },
        devDoc('apps'),
        devDoc('oauth'),
        { type: 'separator' },
        { type: 'section', label: 'Concepts clés' },
        devDoc('pagination'),
        devDoc('filtering-and-sync'),
        devDoc('pricing'),
        devDoc('availability'),
        devDoc('idempotency'),
        devDoc('rate-limits'),
        devDoc('errors'),
        devDoc('webhooks'),
        { type: 'separator' },
        { type: 'section', label: 'Référence' },
        devDoc('changelog'),
        { type: 'link', to: API_PATH, label: 'Référence de l’API', icon: 'braces' },
      ],
    },
  ],

  // The reference sidebar is generated from the OpenAPI tags, and its order is
  // the order of the `tags` array in the document. That is controlled in
  // `apps/api/src/tooling/generate-openapi.ts` — deliberately, rather than with
  // a `navigationRules` sort here, because alphabetical would put Blocks before
  // Calendar and bury Account in the middle.

  apis: {
    type: 'file',
    // An array here (not a single path) is what turns on the version selector.
    // Add `../api/openapi/public-api.v2.json` when v2 ships; v1 stays served.
    input: ['../api/openapi/public-api.v1.json'],
    path: API_PATH,
    // The console's preselected server is set by a processor in
    // `zudoku.build.ts`, so a docs deployment can point at its own environment.
    options: {
      examplesLanguage: 'shell',
      supportedLanguages: [
        { value: 'shell', label: 'cURL' },
        { value: 'javascript', label: 'JavaScript' },
        { value: 'python', label: 'Python' },
        { value: 'php', label: 'PHP' },
      ],
      // The playground calls the sandbox with a key the developer pastes in.
      // Once the developer console lands (DEVELOPER_PLATFORM_ENABLED), this can
      // be swapped for the OAuth integration so it authenticates automatically.
      disablePlayground: false,
      disableSecurity: false,
      showVersionSelect: 'if-available',
      expandAllTags: false,
      schemaDownload: { enabled: true, fileName: 'noe-public-api' },
    },
  },

  // Available in every MDX page without an import, so « Copier la page » and
  // the .md export of guide/nouveautes stay plain text.
  mdx: {
    components: { LiveUpdates },
  },

  docs: {
    files: '/pages/**/*.{md,mdx}',

    // Zudoku prints the « Last modified » line in English with a US date, which
    // reads as broken on a French help centre; off until it can be localised.
    defaultOptions: {
      showLastModified: false,
    },

    /**
     * Publish a plain-Markdown twin of every page (`/guide/menage.md` beside
     * `/guide/menage`). Readers increasingly come through an AI assistant —
     * including the help agent we intend to build on these pages — and Markdown
     * is what an agent can actually consume: no client-side rendering, no chrome.
     */
    publishMarkdown: true,

    llms: {
      // https://llmstxt.org — an index an AI agent can discover and crawl. It
      // lists every page under `pages/`, guides included.
      llmsTxt: true,
      // The whole documentation set in one file, so an agent can load it in a
      // single fetch instead of walking every page.
      llmsTxtFull: true,
      // Nothing here is behind auth today; keep protected routes excluded so
      // enabling auth later cannot silently publish them.
      includeProtected: false,
    },
  },

  /**
   * "Open in Claude / ChatGPT" on each page, which hands the assistant the page
   * URL as context. Left at the built-in presets deliberately: these send only
   * a public documentation URL, never a key or a request body from the
   * playground.
   */
  aiAssistants: ['claude', 'chatgpt'],
};

export default config;
